import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { formatINR } from "@/lib/format";

export default function Login() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data);
      nav("/");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex ip-gradient flex-col justify-between p-12 text-white">
        <Logo variant="light" showTagline />
        <div className="space-y-6">
          <h1 className="text-4xl font-extrabold tracking-tight leading-tight">
            Quick personal loans,<br />on your terms.
          </h1>
          <p className="text-white/80 max-w-md text-base leading-relaxed">
            Apply for {formatINR(1000)} to {formatINR(100000)}. Transparent fees, no surprises — every loan term is reviewed and set by our team before you accept.
          </p>
          <div className="flex items-center gap-2 text-sm text-white/70">
            <ShieldCheck className="h-4 w-4" /> Bank-grade security · Private KYC
          </div>
        </div>
        <p className="text-xs text-white/50">© {new Date().getFullYear()} Instant Paisa</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h2 className="text-2xl font-bold tracking-tight">Welcome back</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-6">Log in to manage your loan application.</p>

          {err && <div data-testid="login-error" className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">{err}</div>}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-brand hover:underline">Forgot password?</Link>
              </div>
              <Input id="password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Log In
            </Button>
          </form>

          <p className="text-sm text-center text-muted-foreground mt-6">
            New to Instant Paisa?{" "}
            <Link to="/register" data-testid="create-account-link" className="font-semibold text-brand hover:underline">Create Account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
